<template>
  <Dialog
    id="preSeasonTeamEditDialog"
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    :closable="!busy"
    style="width: 720px; max-width: 95vw"
  >
    <template #header>
      <div
        class="align-items-center justify-content-center"
        style="font-size: 20px; text-align: center; width: 100%"
      >
        Mannschaft bearbeiten
      </div>
    </template>
    <div v-if="loading" class="flex justify-content-center" style="padding: 20px">
      <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
    </div>
    <div v-else-if="record">
      <h4 style="color: red" v-if="error && error.length > 0">{{ error }}</h4>

      <h3 class="section-header">Mannschaft</h3>
      <div class="flex flex-column">
        <RecordField
          v-for="recordFieldDescription in teamFieldDescriptions"
          v-bind:key="recordFieldDescription.name"
          v-model:record="record"
          :recordFieldDescription="recordFieldDescription"
          :editMode="true"
          :minWidth="300"
        />
      </div>
      <div class="field grid" style="margin-top: 25px">
        <label class="col-fixed" style="width: 180px">Bestehende Mannschaft</label>
        <div class="col">{{ record.existingTeamName || "Neue Mannschaft" }}</div>
      </div>
      <div class="field grid">
        <label class="col-fixed" style="width: 180px">Ligagruppe</label>
        <div class="col">{{ record.leagueGroupPrefix || "Noch nicht zugeordnet" }}</div>
      </div>
      <div class="field grid">
        <label class="col-fixed" style="width: 180px">Bedingungen / Wünsche</label>
        <div class="col">{{ record.numberOfCriterias }}</div>
      </div>

      <h3 class="section-header">Verein</h3>
      <div class="field grid">
        <label class="col-fixed" style="width: 180px">Verein</label>
        <div class="col">
          <Dropdown
            :modelValue="record.association.id"
            :options="options.associations"
            optionLabel="label"
            optionValue="id"
            placeholder="Verein auswählen"
            :filter="true"
            :showClear="true"
            style="width: 100%"
            @change="selectAssociation($event.value)"
          />
        </div>
      </div>
      <div v-if="record.association.id">
        <div class="field grid">
          <label class="col-fixed" style="width: 180px">BWDV-Nummer</label>
          <div class="col">{{ record.association.bwdvNumber || "-" }}</div>
        </div>
        <div class="flex flex-column">
          <RecordField
            v-for="recordFieldDescription in associationFieldDescriptions"
            v-bind:key="recordFieldDescription.name"
            v-model:record="record.association"
            :recordFieldDescription="recordFieldDescription"
            :editMode="true"
            :minWidth="300"
          />
        </div>
        <Message severity="info" :closable="false"
          >Vereinsdaten sind Stammdaten: Änderungen werden direkt am Verein gespeichert und
          wirken sich damit auch auf die laufende Saison aus.</Message
        >
      </div>

      <h3 class="section-header">Ansprechpartner</h3>
      <div class="field grid">
        <label class="col-fixed" style="width: 180px">Bestehender Datensatz</label>
        <div class="col flex align-items-center gap-2">
          <Dropdown
            :modelValue="record.contactPerson.id"
            :options="options.contactPersons"
            optionLabel="label"
            optionValue="id"
            placeholder="Ansprechpartner auswählen"
            :filter="true"
            :showClear="true"
            style="flex: 1 1 auto"
            @change="selectContactPerson($event.value)"
          />
          <Button
            label="Neu"
            icon="pi pi-plus"
            severity="secondary"
            v-tooltip.top="'Neuen Ansprechpartner anlegen'"
            @click="selectContactPerson(null)"
          />
        </div>
      </div>
      <div class="flex flex-column">
        <RecordField
          v-for="recordFieldDescription in contactPersonFieldDescriptions"
          v-bind:key="recordFieldDescription.name"
          v-model:record="record.contactPerson"
          :recordFieldDescription="recordFieldDescription"
          :editMode="true"
          :minWidth="300"
        />
      </div>
      <Message v-if="record.contactPerson.usedInSeason" severity="warn" :closable="false"
        >Dieser Ansprechpartner wird in der laufenden Saison verwendet. Änderungen legen
        beim Speichern automatisch einen neuen Ansprechpartner an, der bestehende bleibt
        unverändert.</Message
      >
      <Message
        v-else-if="record.contactPerson.id"
        severity="info"
        :closable="false"
        >Dieser Ansprechpartner wird nur in der Vorsaison verwendet und wird direkt
        geändert.</Message
      >

      <h3 class="section-header">Spielort</h3>
      <div class="field grid">
        <label class="col-fixed" style="width: 180px">Bestehender Datensatz</label>
        <div class="col flex align-items-center gap-2">
          <Dropdown
            :modelValue="record.location.id"
            :options="options.locations"
            optionLabel="label"
            optionValue="id"
            placeholder="Spielort auswählen"
            :filter="true"
            :showClear="true"
            style="flex: 1 1 auto"
            @change="selectLocation($event.value)"
          />
          <Button
            label="Neu"
            icon="pi pi-plus"
            severity="secondary"
            v-tooltip.top="'Neuen Spielort anlegen'"
            @click="selectLocation(null)"
          />
        </div>
      </div>
      <div class="flex flex-column">
        <RecordField
          v-for="recordFieldDescription in locationFieldDescriptions"
          v-bind:key="recordFieldDescription.name"
          v-model:record="record.location"
          :recordFieldDescription="recordFieldDescription"
          :editMode="true"
          :minWidth="300"
        />
      </div>
      <Message v-if="record.location.usedInSeason" severity="warn" :closable="false"
        >Dieser Spielort wird in der laufenden Saison verwendet. Änderungen legen beim
        Speichern automatisch einen neuen Spielort an, der bestehende bleibt
        unverändert.</Message
      >
      <Message v-else-if="record.location.id" severity="info" :closable="false"
        >Dieser Spielort wird nur in der Vorsaison verwendet und wird direkt
        geändert.</Message
      >
    </div>
    <template #footer>
      <div class="flex justify-content-between flex-wrap card-container">
        <Button
          label="Abbrechen"
          severity="secondary"
          :disabled="busy"
          @click="$emit('update:visible', false)"
        />
        <Button
          label="Speichern"
          icon="pi pi-check"
          :disabled="busy || loading || !record || !record.name"
          :loading="busy"
          @click="save"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import ServerConnection from "../../../services/ServerConnection/ServerConnection";
import RecordField from "../../records/RecordField";
import { preSeasonTeamFieldDescriptions } from "../../records/descriptions/preseasonteam";
import { locationFieldDescriptions } from "../../records/descriptions/location";
import { contactpersonFieldDescriptions } from "../../records/descriptions/contactperson";
import { associationFieldDescriptions } from "../../records/descriptions/association";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Message from "primevue/message";
import ProgressSpinner from "primevue/progressspinner";

// Vereinsfelder, die auch ein Teamvertreter bei der Mannschaftsmeldung bearbeiten darf.
// Name und BWDV-Nummer sind dort ebenfalls nicht änderbar.
const EDITABLE_ASSOCIATION_FIELDS = [
  "sportbundNumber",
  "chairmanName",
  "street",
  "plz",
  "city",
  "email",
];

export default {
  name: "PreSeasonTeamEditDialog",
  components: { Dialog, Dropdown, Button, Message, ProgressSpinner, RecordField },
  props: {
    visible: { type: Boolean, default: false },
    teamId: { type: Number, default: null },
  },
  emits: ["update:visible", "saved"],
  data() {
    return {
      record: null,
      options: { locations: [], associations: [], contactPersons: [] },
      loading: false,
      busy: false,
      error: "",
    };
  },
  computed: {
    teamFieldDescriptions() {
      return preSeasonTeamFieldDescriptions;
    },
    locationFieldDescriptions() {
      return locationFieldDescriptions;
    },
    contactPersonFieldDescriptions() {
      return contactpersonFieldDescriptions;
    },
    associationFieldDescriptions() {
      return associationFieldDescriptions.filter((fd) =>
        EDITABLE_ASSOCIATION_FIELDS.includes(fd.name)
      );
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.openDialog();
      }
    },
  },
  methods: {
    openDialog() {
      this.error = "";
      this.busy = false;
      this.record = null;
      this.loadOptions();
      this.loadTeam();
    },
    loadOptions() {
      if (this.options.locations.length > 0) {
        return;
      }
      ServerConnection.get("preSeasonTeamEditOptions")
        .then((response) => {
          this.options = response.data;
        })
        .catch((err) => {
          this.error = "Konnte Auswahllisten nicht laden: " + this.errorMessage(err);
        });
    },
    loadTeam() {
      if (!this.teamId) {
        return;
      }
      this.loading = true;
      ServerConnection.get("preSeasonTeam/" + this.teamId)
        .then((response) => {
          var record = response.data;
          // Die abhängigen Datensätze immer als Objekt vorhalten, damit die
          // Eingabefelder auch ohne hinterlegten Datensatz gebunden werden können.
          record.location = record.location || this.emptyLocation();
          record.contactPerson = record.contactPerson || this.emptyContactPerson();
          record.association = record.association || this.emptyAssociation();
          this.record = record;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          this.error = "Konnte Mannschaft nicht laden: " + this.errorMessage(err);
        });
    },
    // Die Auswahl überträgt die Werte immer in das bestehende Objekt (statt es zu
    // ersetzen), damit die bereits gerenderten Eingabefelder weiter darauf zeigen.
    selectLocation(locationId) {
      var selected = this.options.locations.find((l) => l.id === locationId);
      Object.assign(this.record.location, selected || this.emptyLocation());
    },
    selectContactPerson(contactPersonId) {
      var selected = this.options.contactPersons.find((c) => c.id === contactPersonId);
      Object.assign(this.record.contactPerson, selected || this.emptyContactPerson());
    },
    selectAssociation(associationId) {
      var selected = this.options.associations.find((a) => a.id === associationId);
      Object.assign(this.record.association, selected || this.emptyAssociation());
    },
    emptyLocation() {
      return {
        id: null,
        label: null,
        name: null,
        street: null,
        plz: null,
        city: null,
        numberOfBoards: null,
        smokingType: null,
        youthAllowed: null,
        additionalInformations: null,
        usedInSeason: false,
      };
    },
    emptyContactPerson() {
      return {
        id: null,
        label: null,
        name: null,
        email: null,
        phoneNumber: null,
        usedInSeason: false,
      };
    },
    emptyAssociation() {
      return {
        id: null,
        label: null,
        name: null,
        bwdvNumber: null,
        sportbundNumber: null,
        chairmanName: null,
        street: null,
        plz: null,
        city: null,
        email: null,
      };
    },
    save() {
      if (!this.record || !this.record.id) {
        return;
      }
      this.error = "";
      this.busy = true;
      ServerConnection.put(
        "preSeasonTeam/" + this.record.id,
        JSON.stringify(this.record)
      )
        .then((response) => {
          this.busy = false;
          this.$emit("saved", response.data);
          this.$emit("update:visible", false);
        })
        .catch((err) => {
          this.busy = false;
          this.error = "Speichern fehlgeschlagen: " + this.errorMessage(err);
        });
    },
    errorMessage(err) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string"
          ? err.response.data
          : JSON.stringify(err.response.data);
      }
      if (err && err.message) {
        return err.message;
      }
      return "Unbekannter Fehler";
    },
  },
};
</script>

<style lang="css" scoped>
.section-header {
  margin: 25px 0 10px 0;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--surface-border, #dee2e6);
}
</style>
